import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Row,
  Col,
  Badge,
  Alert,
  Spin,
  Tag,
  InputNumber,
  Typography,
  message,
} from "antd";
import { InfoCircleOutlined, DollarOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { uploadFileToS3 } from "../../../utils/s3Upload";
import { extractRctInput } from "../utils/engineDataExtraction";
import { StepUploadData } from "./StepUpload";

const { Text } = Typography;
const { Option } = Select;

const MAX_DURATIONS = 5;
const MIN_DURATION_WEEKS = 4;
const MAX_DURATION_WEEKS = 8;

export interface StepConfigurationData {
  jobName: string;
  // Bid Optimizer Test Setup
  campaignObjective: string;
  customerIdentifier: string;
  targetVariable: string;
  age?: string;
  gender?: string;
  devicePlatform?: string;
  geoLocationType: "dma" | "region" | "state";
  region: string;
  locale?: string;
  userOs?: string;
  // RCT Test Setup
  dateColumn: string;
  durations: number[];
  weeklyCap: number;
  totalWeeklySpend: number;
  isSales: boolean;
}

interface StepConfigurationProps {
  stepUploadData?: StepUploadData | null;
  jobConfigId: string;
  onNext: (data: StepConfigurationData) => void;
  onPrevious: () => void;
  onNameChange?: (name: string) => void;
  existingData?: StepConfigurationData | null;
  // See causal-loop/components/StepConfiguration.tsx's identical prop for the
  // full rationale - read-only render from the job's saved config, Next just
  // advances rather than re-submitting.
  viewMode?: boolean;
}

const guessColumn = (headers: string[], candidates: RegExp[]): string | undefined => {
  for (const pattern of candidates) {
    const match = headers.find((h) => pattern.test(h));
    if (match) return match;
  }
  return undefined;
};

// Step 2 of the RCT variant of Causal Loop - same three-block layout as
// causal-loop/components/StepConfiguration.tsx (General Information -> Bid
// Optimizer Test Setup -> [Geo|RCT] Test Setup), but the third block is
// replaced with RCT Test Setup: candidate durations, weekly investment cap,
// and total weekly BAU spend - the same three inputs
// rct-experiment/components/SetupStep.tsx collects, picked from the RCT
// Experiment feature per the user's ask. No campaign start date, SCM type, or
// market selection here - the RCT 50/50 design engine screens/pairs every
// location in the file itself and needs none of those. Split is always 50/50
// (per this feature's name), so it isn't exposed as a choice.
const StepConfiguration: React.FC<StepConfigurationProps> = ({
  stepUploadData,
  jobConfigId,
  onNext,
  onPrevious,
  onNameChange,
  existingData,
  viewMode,
}) => {
  const headers = stepUploadData?.headers || [];
  const [form] = Form.useForm();

  const dateColumn = useMemo(
    () => existingData?.dateColumn || guessColumn(headers, [/date/i, /week/i, /^day$/i, /period/i]) || "",
    [headers, existingData?.dateColumn]
  );

  const [durations, setDurations] = useState<number[]>(existingData?.durations || []);
  const [durationInput, setDurationInput] = useState("");
  const [durationError, setDurationError] = useState<string | null>(null);
  const [weeklyCap, setWeeklyCap] = useState<number | null>(existingData?.weeklyCap ?? null);
  const [totalWeeklySpend, setTotalWeeklySpend] = useState<number | null>(
    existingData?.totalWeeklySpend ?? null
  );
  const [preparingRctData, setPreparingRctData] = useState(false);
  const [rctDataReady, setRctDataReady] = useState(false);
  const [rctDataError, setRctDataError] = useState<string | null>(null);
  const lastPreparedKeyRef = useRef<string>("");

  // Re-syncs whenever existingData arrives asynchronously (view mode fetches
  // it after mount) - see causal-loop/components/StepConfiguration.tsx's
  // identical effect for the full rationale.
  useEffect(() => {
    if (!existingData) return;
    setDurations(existingData.durations || []);
    setWeeklyCap(existingData.weeklyCap ?? null);
    setTotalWeeklySpend(existingData.totalWeeklySpend ?? null);
  }, [existingData]);

  const region = Form.useWatch("region", form);
  const targetVariable = Form.useWatch("targetVariable", form);
  const campaignObjective = Form.useWatch("campaignObjective", form);
  // Same derivation as causal-loop/components/StepConfiguration.tsx - one
  // field drives both engines' notion of "is this a sales test" (memory:
  // project_isSales).
  const isSales = campaignObjective === "sales";

  const durationsFull = durations.length >= MAX_DURATIONS;

  const addDuration = (raw: string) => {
    const n = parseInt(raw.trim(), 10);
    if (!Number.isInteger(n)) return;
    if (n < MIN_DURATION_WEEKS || n > MAX_DURATION_WEEKS) {
      setDurationError(`Enter a duration between ${MIN_DURATION_WEEKS} and ${MAX_DURATION_WEEKS} weeks.`);
      return;
    }
    setDurationError(null);
    if (durations.includes(n)) return;
    if (durations.length >= MAX_DURATIONS) return;
    setDurations((prev) => [...prev, n]);
  };

  const handleDurationKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addDuration(durationInput);
      setDurationInput("");
    } else if (e.key === "Backspace" && durationInput === "" && durations.length > 0) {
      setDurations((prev) => prev.slice(0, -1));
    }
  };

  const removeDuration = (value: number) => {
    setDurations((prev) => prev.filter((d) => d !== value));
  };

  // Once Date Column + Region + Target Variable are all known, aggregates the
  // shared file to the weekly date/location/Y shape the RCT design engine
  // expects and uploads it as this job's rct_experiment_data.csv - same
  // "prepare then upload" pattern as causal-loop's Geo Lift data prep, using
  // extractRctInput (which itself reuses rct-experiment's own aggregation
  // logic - see engineDataExtraction.ts). Re-runs if the user changes any of
  // the three fields afterward.
  useEffect(() => {
    if (viewMode || !stepUploadData?.dataFile) return;
    if (!dateColumn || !region || !targetVariable) return;
    const key = `${dateColumn}|${region}|${targetVariable}`;
    if (lastPreparedKeyRef.current === key) return;
    lastPreparedKeyRef.current = key;

    (async () => {
      setPreparingRctData(true);
      setRctDataError(null);
      setRctDataReady(false);
      try {
        const aggregatedFile = await extractRctInput(
          stepUploadData!.dataFile,
          dateColumn,
          region,
          targetVariable
        );
        await uploadFileToS3(aggregatedFile, `${jobConfigId}/input/rct_experiment_data.csv`);
        setRctDataReady(true);
      } catch (err) {
        console.error("Error preparing RCT data:", err);
        setRctDataError("Couldn't prepare RCT data from your file. Please try again or go back to Step 1.");
      } finally {
        setPreparingRctData(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateColumn, region, targetVariable]);

  const canProceedRct = useMemo(() => {
    return Boolean(
      dateColumn &&
        durations.length > 0 &&
        weeklyCap !== null &&
        weeklyCap > 0 &&
        totalWeeklySpend !== null &&
        totalWeeklySpend > 0 &&
        rctDataReady
    );
  }, [dateColumn, durations, weeklyCap, totalWeeklySpend, rctDataReady]);

  const handleFinish = (values: any) => {
    if (!dateColumn) {
      message.error("No date column could be detected in your file - please go back and upload a file with a date/time column.");
      return;
    }
    if (preparingRctData) {
      message.info("Still preparing your RCT data - just a moment.");
      return;
    }
    if (!canProceedRct) {
      message.error("Please complete the RCT Test Setup fields below (candidate durations, weekly investment cap, and total weekly spend).");
      return;
    }
    onNext({
      jobName: values.jobName.trim(),
      campaignObjective: values.campaignObjective,
      customerIdentifier: values.customerIdentifier,
      targetVariable: values.targetVariable,
      age: values.age,
      gender: values.gender,
      devicePlatform: values.devicePlatform,
      geoLocationType: values.geoLocationType || "dma",
      region: values.region,
      locale: values.locale,
      userOs: values.userOs,
      dateColumn,
      durations,
      weeklyCap: weeklyCap as number,
      totalWeeklySpend: totalWeeklySpend as number,
      isSales,
    });
  };

  const handleValuesChange = (changed: any) => {
    if (changed.jobName !== undefined) onNameChange?.(changed.jobName);
  };

  const viewOnlyHeaders = useMemo(
    () =>
      Array.from(
        new Set(
          [
            existingData?.customerIdentifier,
            existingData?.targetVariable,
            existingData?.age,
            existingData?.gender,
            existingData?.devicePlatform,
            existingData?.region,
            existingData?.locale,
            existingData?.userOs,
          ].filter(Boolean) as string[]
        )
      ),
    [existingData]
  );
  const columnOptions = (viewMode ? viewOnlyHeaders : headers).map((h) => (
    <Option key={h} value={h}>
      {h}
    </Option>
  ));

  if (viewMode && !existingData) {
    return (
      <div className="text-center py-24">
        <Spin size="large" tip="Loading saved configuration..." />
      </div>
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      disabled={viewMode}
      onFinish={handleFinish}
      onValuesChange={handleValuesChange}
      initialValues={{
        jobName: existingData?.jobName || "",
        campaignObjective: existingData?.campaignObjective || "sales",
        customerIdentifier:
          existingData?.customerIdentifier ||
          guessColumn(headers, [/cust.*id/i, /customer.*id/i, /user.*id/i, /^id$/i]),
        targetVariable:
          existingData?.targetVariable || guessColumn(headers, [/target/i, /sales/i, /revenue/i, /^y$/i]),
        age: existingData?.age || guessColumn(headers, [/age/i]),
        gender: existingData?.gender || guessColumn(headers, [/gender/i]),
        devicePlatform: existingData?.devicePlatform || guessColumn(headers, [/device/i]),
        geoLocationType: existingData?.geoLocationType || "dma",
        region:
          existingData?.region ||
          guessColumn(headers, [/location/i, /dma/i, /region/i, /city/i, /state/i, /geo/i]),
        locale: existingData?.locale || guessColumn(headers, [/locale/i]),
        userOs: existingData?.userOs || guessColumn(headers, [/^os$|user.*os/i]),
      }}
    >
      {/* --- Block 1: General Information --- */}
      <Card title={<span className="text-lg font-semibold text-gray-900">General Information</span>} className="mb-6 shadow-sm">
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="jobName"
              label="Job Name"
              rules={[
                { required: true, message: "Please enter a name" },
                { max: 120, message: "Name cannot exceed 120 characters" },
              ]}
              tooltip={{
                title: "One study, two engines - covers both Bid Optimizer and RCT 50/50 results.",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Input placeholder="e.g. Fall Sales Push - Bid + RCT Study" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="campaignObjective"
              label="Campaign Objective"
              rules={[{ required: true, message: "Please select an objective" }]}
              tooltip={{
                title:
                  "Drives Bid Optimizer's optimization target and whether results are shown as Sales ($) or iROAS.",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Select
                options={[
                  { label: "Sales", value: "sales" },
                  { label: "Lead Generation", value: "lead" },
                  { label: "Reach", value: "reach" },
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* --- Block 2: Bid Optimizer Test Setup --- */}
      <Card title={<span className="text-lg font-semibold text-gray-900">Bid Optimizer Test Setup</span>} className="mb-6 shadow-sm">
        <div className="flex items-center gap-2 mb-3">
          <Text strong className="text-base font-semibold text-gray-800">Required Variables</Text>
          <Badge count="Required" style={{ backgroundColor: "#1890ff" }} />
        </div>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="customerIdentifier"
              label="Customer Identifier"
              rules={[{ required: true, message: "Please select a Customer Identifier column" }]}
              tooltip={{
                title: "Customer id for unique identification of records. Eg: Id or email",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Select placeholder="Select column" showSearch optionFilterProp="children">
                {columnOptions}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="targetVariable"
              label="Target Variable"
              rules={[{ required: true, message: "Please select a Target Variable column" }]}
              tooltip={{
                title: "Column the model optimizes against - also used as the RCT design's KPI. Eg: Sales",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Select placeholder="Select column" showSearch optionFilterProp="children">
                {columnOptions}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div className="flex items-center gap-2 mb-3 mt-4">
          <Text strong className="text-base font-semibold text-gray-800">Demographic Variables</Text>
          <Badge count="Recommended" style={{ backgroundColor: "#52c41a" }} />
        </div>
        <Row gutter={24}>
          <Col span={8}>
            <Form.Item name="age" label="Age Range">
              <Select placeholder="Not detected" allowClear showSearch optionFilterProp="children">
                {columnOptions}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="gender" label="Gender">
              <Select placeholder="Not detected" allowClear showSearch optionFilterProp="children">
                {columnOptions}
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="locale" label="Locale">
              <Select placeholder="Not detected" allowClear showSearch optionFilterProp="children">
                {columnOptions}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div className="flex items-center gap-2 mb-3 mt-4">
          <Text strong className="text-base font-semibold text-gray-800">Geographic Variables</Text>
          <Badge count="Recommended" style={{ backgroundColor: "#52c41a" }} />
        </div>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="geoLocationType"
              label="Geo Location Type"
              tooltip={{
                title: "Select the geo location type",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Select
                placeholder="Select type"
                allowClear
                options={[
                  { label: "Designated Market Area", value: "dma" },
                  { label: "Regions", value: "region" },
                  { label: "States", value: "state" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="region"
              label="Region / Geo Location"
              rules={[{ required: true, message: "Please select the Region/Geo Location column" }]}
              tooltip={{
                title: "This column is also used as the RCT design's location column below.",
                icon: <InfoCircleOutlined />,
              }}
            >
              <Select placeholder="Select column" showSearch optionFilterProp="children">
                {columnOptions}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div className="flex items-center gap-2 mb-3 mt-4">
          <Text strong className="text-base font-semibold text-gray-800">Device Variables</Text>
          <Badge count="Optional" style={{ backgroundColor: "#faad14" }} />
        </div>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item name="devicePlatform" label="Device Platform">
              <Select placeholder="Not detected" allowClear showSearch optionFilterProp="children">
                {columnOptions}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="userOs" label="User OS">
              <Select placeholder="Not detected" allowClear showSearch optionFilterProp="children">
                {columnOptions}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* --- Block 3: RCT Test Setup --- */}
      <h3 className="text-lg font-semibold text-gray-900 mb-3">RCT Test Setup</h3>

      {!dateColumn && (
        <Alert
          className="mb-4"
          type="error"
          showIcon
          message="No date/time column detected"
          description="The RCT 50/50 design needs a weekly date column per location to build the randomized split - none of this file's columns look like a date. Please go back to Step 1 and upload a file that includes one."
        />
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow md:col-span-2">
          <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-3">
            <ClockCircleOutlined className="text-indigo-600" />
            Candidate Test Durations (weeks)
            <span className="text-red-500">*</span>
            {preparingRctData && (
              <span className="flex items-center gap-2 text-sm font-normal text-indigo-600 ml-2">
                <Spin size="small" /> Preparing RCT data...
              </span>
            )}
          </label>
          {rctDataError && <Alert type="error" showIcon message={rctDataError} className="mb-3" />}
          <Input
            size="large"
            value={durationInput}
            onChange={(e) => {
              setDurationError(null);
              setDurationInput(e.target.value.replace(/[^\d]/g, ""));
            }}
            onKeyDown={handleDurationKeyDown}
            placeholder={
              durationsFull
                ? `Maximum ${MAX_DURATIONS} durations reached`
                : `Type a number of weeks (${MIN_DURATION_WEEKS}-${MAX_DURATION_WEEKS}) and press Enter`
            }
            status={durationError ? "error" : undefined}
            disabled={viewMode || durationsFull}
          />
          {durationError && <p className="text-sm text-red-600 mt-2">{durationError}</p>}
          {durations.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {durations.map((d) => (
                <Tag
                  key={d}
                  closable={!viewMode}
                  onClose={() => removeDuration(d)}
                  className="!text-sm !py-1 !px-2.5"
                >
                  {d} wks
                </Tag>
              ))}
            </div>
          )}
          <p className="text-gray-500 text-sm mt-2">
            Add up to {MAX_DURATIONS} durations, each between {MIN_DURATION_WEEKS} and {MAX_DURATION_WEEKS}{" "}
            weeks. The design searches all of them and uses whichever fits best under your spend cap.
          </p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-3">
            <DollarOutlined className="text-green-600" />
            Weekly Incremental Spend Cap
            <span className="text-red-500">*</span>
          </label>
          <InputNumber
            className="w-full rounded-lg"
            prefix="$"
            size="large"
            min={0}
            step={1000}
            placeholder="e.g. 80,000"
            value={weeklyCap ?? undefined}
            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            onChange={(v) => setWeeklyCap(v as number)}
            disabled={viewMode}
          />
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
          <label className="flex items-center gap-2 text-base font-semibold text-gray-800 mb-3">
            <DollarOutlined className="text-green-600" />
            Total Weekly Business As Usual Ad Spend
            <span className="text-red-500">*</span>
          </label>
          <InputNumber
            className="w-full rounded-lg"
            prefix="$"
            size="large"
            min={0}
            step={10000}
            placeholder="e.g. 1,000,000"
            value={totalWeeklySpend ?? undefined}
            formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
            onChange={(v) => setTotalWeeklySpend(v as number)}
            disabled={viewMode}
          />
        </div>
      </div>

      <div className="flex justify-between">
        {/* Explicit disabled={false} - see causal-loop's identical comment:
            antd Button otherwise inherits disabled=true from <Form disabled={viewMode}>. */}
        <Button size="large" onClick={onPrevious} disabled={false}>
          Previous
        </Button>
        {viewMode ? (
          <Button
            type="primary"
            size="large"
            disabled={false}
            onClick={() => onNext(existingData as StepConfigurationData)}
          >
            Next
          </Button>
        ) : (
          <Button type="primary" size="large" htmlType="submit" disabled={!canProceedRct}>
            Calculate Power
          </Button>
        )}
      </div>
    </Form>
  );
};

export default StepConfiguration;
